import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { MessageTypeFilter } from "@sendbird/chat/message";
import type { FileMessage } from "@sendbird/chat/message";
import { getUser } from "../sendbird.hooks";
import { getSendbird } from "../sendbird";
import { SendbirdUser } from "../chat";
import { SVGPlayIcon } from "../icons";
import PlayVideoModal from "../components/playVideoModal";
import {
    BtnOpenChat,
    ChatCell,
    ChatCellIcon,
    ChatCellLabel,
    ChatIndicatorSkeleton,
    ChatIndicatorWrapper,
    ChatWrapperButton,
    EmptyState,
    EmptyStateText,
    MessageCounter,
    PageGrid,
    PlayOverlay,
    SubscriberPageWrapper,
    VideoCardSkeleton,
    VideoCell,
    VideoCellMeta,
    VideoDate,
    VideoGrid,
    VideoName,
    VideoSkeletonButton,
    VideoSkeletonDate,
    VideoSkeletonMeta,
    VideoSkeletonName,
    VideoSkeletonThumbnail,
    VideoThumbnail,
    VideoThumbnailFallback,
    VideoThumbnailWrapper,
    WatchVideoButton,
} from "./index.styled";
import ChatIndicator from "./components/chatIndicator";
import { pgVIPViewed, pgVIPPlayedSwing } from "../../mixPanelEvents/mixPanelEvents";

export interface SubscriberPageProps {
    user: SendbirdUser | null;
    onOpenChat: () => void;
    uploadesIndicator: 'my_uploads' | 'sent_from_coach';
    onUnreadCount?: (count: number) => void;
}

const formatDate = (ts: number) =>
    new Date(ts).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

const SubscriberPage: React.FC<SubscriberPageProps> = ({ user, onOpenChat, uploadesIndicator, onUnreadCount }) => {
    const [videos, setVideos] = useState<FileMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | undefined>(undefined);
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);
    const [unreadCount, setUnreadCount] = useState<number>(0);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                if (!cancelled && user) setCurrentUserId(user.user_id);
                const sb = await getSendbird();
                await sb.connect(user!.user_id, user!.access_token);
                const channel = await sb.groupChannel.getChannel(user!.channel_url);
                const count = channel.unreadMessageCount;
                if (!cancelled) {
                    setUnreadCount(count);
                    onUnreadCount?.(count);
                }
                const collected: FileMessage[] = [];
                const query = channel.createPreviousMessageListQuery({
                    limit: 100,
                    reverse: false,
                    messageTypeFilter: MessageTypeFilter.FILE,
                });
                while (query.hasNext) {
                    const batch = await query.load();
                    collected.push(...(batch as FileMessage[]));
                }
                if (!cancelled) setVideos(collected.filter(m => m.type?.startsWith('video/')));

                pgVIPViewed('My Uploads');
            } catch (err) {
                console.error("Failed to load videos:", err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, []);

    // Build the grid items filtered by upload indicator
    const buildGridItems = (): Array<{ type: 'video'; msg: FileMessage }> => {
        const filtered = videos.filter((msg) => {
            if (uploadesIndicator === 'my_uploads') {
                return msg.sender?.userId === currentUserId;
            }
            if (uploadesIndicator === 'sent_from_coach') {
                return msg.sender?.userId !== currentUserId;
            }
            return true;
        });
        return filtered.map((msg) => ({ type: 'video' as const, msg }));
    };

    const gridItems = buildGridItems();

    return (
        <SubscriberPageWrapper>
            <PageGrid>
                {loading ? (
                <VideoGrid>
                    {Array.from({ length: 3 }).map((_, i) => (
                        <VideoCardSkeleton key={i}>
                            <VideoSkeletonThumbnail variant="rectangular" />
                            <VideoSkeletonMeta>
                                <VideoSkeletonName variant="rectangular" />
                                <VideoSkeletonDate variant="rectangular" />
                            </VideoSkeletonMeta>
                            <VideoSkeletonButton variant="rectangular" />
                        </VideoCardSkeleton>
                    ))}
                </VideoGrid>
            ) : (
                <VideoGrid>
                    {gridItems.map((item, index) =>
                        <VideoCell key={item.msg.messageId}>
                            <VideoThumbnailWrapper>
                                {item.msg.thumbnails?.[0]?.url ? (
                                    <VideoThumbnail
                                        src={item.msg.thumbnails[0].url}
                                        alt={item.msg.name ?? 'Swing video'}
                                    />
                                ) : (
                                    <VideoThumbnailFallback />
                                )}
                                <PlayOverlay className="play-overlay">
                                    {SVGPlayIcon}
                                </PlayOverlay>
                            </VideoThumbnailWrapper>
                            <VideoCellMeta>
                                <VideoName>{item.msg.name ?? 'Swing video'}</VideoName>
                                <VideoDate>{formatDate(item.msg.createdAt)}</VideoDate>
                            </VideoCellMeta>
                            <WatchVideoButton
                                disableElevation
                                onClick={() => {
                                    setSelectedVideoUrl(item.msg.url);
                                    pgVIPPlayedSwing({
                                        'Video ID': item.msg.messageId,
                                        Source: uploadesIndicator === 'my_uploads' ? 'Uploaded Swings' : 'Coach Videos',
                                        Type: 'Member',
                                    });
                                }}
                            >
                                Watch Video
                            </WatchVideoButton>
                        </VideoCell>
                    )}
                </VideoGrid>
            )}

            </PageGrid>

            <ChatIndicatorWrapper>
                {loading
                    ? <ChatIndicatorSkeleton variant="rectangular" />
                    : <ChatIndicator onOpenChat={onOpenChat} unreadCount={unreadCount} />
                }
            </ChatIndicatorWrapper>

            <PlayVideoModal
                open={!!selectedVideoUrl}
                videoUrl={selectedVideoUrl}
                onClose={() => setSelectedVideoUrl(undefined)}
            />
        </SubscriberPageWrapper>
    );
};

export default SubscriberPage;
